import React, { useEffect, useState } from "react";
import {
  makeStyles,
  tokens,
  Spinner,
  Text,
  Card,
} from "@fluentui/react-components";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { useAuth, useIsMobile } from "../hooks";
import {
  POWERBI_EMBED_SCOPE,
  POWERBI_REPORT_ID,
  POWERBI_WORKSPACE_ID,
} from "../config/auth";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    overflow: "hidden",
  },
  card: {
    flexGrow: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  embed: {
    flexGrow: 1,
    height: "100%",
    "& iframe": {
      border: "none",
      height: "100%",
      width: "100%",
    },
  },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: tokens.spacingVerticalM,
  },
  error: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: tokens.spacingVerticalM,
    color: tokens.colorPaletteRedForeground1,
  },
});

export const PowerBIReport: React.FC = () => {
  const styles = useStyles();
  const { getAccessToken, isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const [embedToken, setEmbedToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    if (!POWERBI_REPORT_ID || !POWERBI_WORKSPACE_ID) {
      setError(
        "Power BI report configuration missing. Set VITE_POWERBI_REPORT_ID and VITE_POWERBI_WORKSPACE_ID in your .env file.",
      );
      setIsLoading(false);
      return;
    }

    const acquireToken = async () => {
      setIsLoading(true);
      setError(null);
      const token = await getAccessToken(POWERBI_EMBED_SCOPE);
      if (token) {
        setEmbedToken(token);
      } else {
        setError("Failed to acquire Power BI access token.");
      }
      setIsLoading(false);
    };

    acquireToken();
  }, [isAuthenticated, getAccessToken]);

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" />
        <Text>Loading report...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <Text weight="semibold">{error}</Text>
      </div>
    );
  }

  if (!embedToken) {
    return null;
  }

  const embedUrl = `https://app.powerbi.com/reportEmbed?reportId=${POWERBI_REPORT_ID}&groupId=${POWERBI_WORKSPACE_ID}`;

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <PowerBIEmbed
          embedConfig={{
            type: "report",
            id: POWERBI_REPORT_ID,
            embedUrl,
            accessToken: embedToken,
            tokenType: models.TokenType.Aad,
            settings: {
              layoutType: isMobile
                ? models.LayoutType.MobilePortrait
                : models.LayoutType.Custom,
              customLayout: isMobile
                ? undefined
                : { displayOption: models.DisplayOption.FitToPage },
              panes: {
                filters: { expanded: false, visible: false },
                pageNavigation: { visible: false },
                bookmarks: { visible: false },
              },
              bars: {
                actionBar: { visible: false },
                statusBar: { visible: false },
              },
              background: models.BackgroundType.Transparent,
            },
          }}
          cssClassName={styles.embed}
        />
      </Card>
    </div>
  );
};
